import React, { useState } from 'react';
import { translate, InjectedTranslateProps, Trans } from 'react-i18next';
import {
  Page,
  Container,
  Row,
  Col,
  Card,
  Box,
  Breadcrumbs,
  Modal,
  ModalPreviewLayout,
  TextButton,
  Text,
  IconButton,
  Tooltip
} from 'wix-style-react';
import {
  datahooks,
  BMComponentNames,
  sections,
  CRM_HOME_ORIGIN,
  CRM_HOME,
  referralInfoBuilder,
  experimentNames,
  preferredRecipes,
  CRM_VIDEO_URL,
} from '../constants';
import {
  PageComponentId,
  navigateTo,
  INavigateToPageBase,
} from '@wix/business-manager-api';
import { ModuleRegistry } from 'react-module-container';
import { CrmHomeBiLogger } from '../../bi-logger';
import Experiments from '@wix/wix-experiments';

import { CrmHomeSection } from '../CrmHomeSection/CrmHomeSection';
import { ToolCard } from '../ToolCard/ToolCard';
import { FinanceSection } from './sections/finances/Finance.section';
import { WorkflowsSection } from './sections/workflows/WorkflowsSection';
import { OpportunitySection } from './sections/opportunities/OpportunitySection';
import { Banner } from '../Banner/Banner';
import { Promote, Play } from 'wix-ui-icons-common';
import {
  CrmHomeBannerOffer,
  CrmButtonActionType,
  CrmButtonAction,
  InvokeCTAOpts,
  CardDealerOffer,
  FinanceAppData,
} from '../../types';
import { CreateNewMenu } from '../CreateNewMenu/CreateNewMenu';
import { IBMModuleParams } from '../../config';
import { WorkflowServiceApi } from '../../services/workflows.service';
import { Integrations } from './sections/integrations/Integrations';
import { LazyWidgetCard } from '../LazyWidgetCard/LazyWidgetCard';
import { ScrollAreaData } from 'wix-style-react/dist/es/src/common/ScrollableContainer';

enum DemoVideoType {
  NONE,
  BANNER,
  CRM,
}

export interface CrmHomeProps extends InjectedTranslateProps {
  tools: any;
  biLogger: CrmHomeBiLogger;
  experiments: Experiments;
  banner: CrmHomeBannerOffer;
  cards: CardDealerOffer[];
  widgetInitData: { contacts: any };
  connections: { [x: string]: boolean };
  financeIntegrationData: FinanceAppData[];
  moduleParams: Partial<IBMModuleParams>;
  services: { workflows: WorkflowServiceApi };
}

let lastAreaY = 'top'
const YAreas = ['bottom', 'middle', 'top'];

const CrmHomeComponent: React.FC<CrmHomeProps> = (props) => {
  const {
    t,
    tools,
    biLogger,
    experiments,
    moduleParams,
    widgetInitData,
  } = props;
  const [demoVideo, setDemoVideo] = useState(DemoVideoType.NONE);

  const phase2NewFeatures = experiments.enabled(
    experimentNames.phase2newFetures,
  );

  const shouldShowCrmVideo = experiments.enabled(
    experimentNames.crmVideo,
  );

  const logAndNavigate = (tool: any, sectionName: string) => {
    biLogger.reportToolCardClicked(tool.name);
    const referralInfo = referralInfoBuilder(sectionName, tool.name);
    tool.navigate(referralInfo);
  };

  const AutomationsDashboard = ModuleRegistry.component(
    BMComponentNames.automationsDashboard,
  );
  const AscendUpgradeButton = ModuleRegistry.component(
    BMComponentNames.ascendUpgradeButton,
  );
  const TasksWidget = ModuleRegistry.component(BMComponentNames.tasksWidget);
  const ContactsWidget = ModuleRegistry.component(
    BMComponentNames.contactsWidget,
  );
  const AscendBadge = ModuleRegistry.component(BMComponentNames.ascendBadge);

  const mapDemoVideoStateToUrls = (): string => {
    switch (demoVideo) {
      case DemoVideoType.NONE:
        return "";

      case DemoVideoType.BANNER:
        return props.banner.video || "";

      case DemoVideoType.CRM:
        return CRM_VIDEO_URL;

      default:
        return "";
    }
  };

  const closeDemoVideo = () => {
    setDemoVideo(DemoVideoType.NONE);
  };

  const openDemoVideo = (video: DemoVideoType) => {
    biLogger.reportCrmHomeVideo();
    setDemoVideo(video);
  };

  const renderPromoVideoModal = () => {
    if (demoVideo !== DemoVideoType.NONE) {
      // A11Y - enables closing the video by click on the Escape btn.
      document.addEventListener(
        'keyup',
        (e) => {
          if (e.key === 'Escape') {
            closeDemoVideo();
          }
        },
        {
          once: true,
        },
      );
    }
    return (
      <Modal isOpen={demoVideo !== DemoVideoType.NONE} dataHook={datahooks.demoVideoModal}>
        <ModalPreviewLayout
          onClose={() => closeDemoVideo()}
          shouldCloseOnOverlayClick={true}
        >
          <Box verticalAlign="middle" height="90%">
            <iframe
              title="promotional video"
              width="768"
              height="432"
              src={mapDemoVideoStateToUrls()}
              frameBorder="0"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
              data-aid="demo-video-modal-iframe"
            />
          </Box>
        </ModalPreviewLayout>
      </Modal>
    );
  };

  const AutomationsDashboardProps = {
    appName: t('crmHomePage.header.title'),
    backToConfig: { pageComponentId: PageComponentId.CrmHome },
    recipeView: true,
    preferredRecipes,
  };

  const onCreateNewAutomation = () => {
    biLogger.reportCardButtonClicked('automations', 'create-your-own');
    navigateTo({
      pageComponentId: PageComponentId.Triggers,
      contextData: { appState: 'rules/create', referrer: 'crmHome' },
    });
  };

  const reportBannerNavigation = (
    payload: INavigateToPageBase,
    contactId?: string,
  ) => {
    switch (payload.pageComponentId) {
      case PageComponentId.Engage:
        if (contactId) {
          biLogger.reportSendMessageTo(contactId);
        }
        break;
      case PageComponentId.ContactsImport:
        biLogger.reportImportContacts();
        break;
      default:
        // other compoennts with no reporting
        break;
    }
  };

  const onBannerButtonClick = (btn: CrmButtonAction, contactId?: string) => {
    switch (btn.value.type) {
      case CrmButtonActionType.INVOKE:
        const options = btn.value as InvokeCTAOpts;
        ModuleRegistry.invoke(options.method, options.payload || {});
        break;

      case CrmButtonActionType.NAVIGATE:
        reportBannerNavigation(btn.value.payload, contactId);
        if (btn.value.withContactId) {
          btn.value.payload.contextData.appState = contactId;
        }
        navigateTo(btn.value.payload);
        break;

      default:
        // Possibly log unknown btn type
        break;
    }
  };

  const renderBanner = () => {
    const { banner } = props;
    const {
      offerName,
      title,
      body,
      mainButtonText,
      mainButtonConfig,
      secondaryButtonConfig,
      secondaryButtonText,
      video,
      illustration,
    } = banner;
    return (
      <Banner
        title={t(title)}
        decription={t(body)}
        mainActionTitle={t(mainButtonText)}
        secActionTitle={secondaryButtonText && t(secondaryButtonText)}
        onOpenVideoModal={() =>
          props.banner.video && openDemoVideo(DemoVideoType.BANNER)
        }
        onMainActionClick={(contactId) => {
          biLogger.reportCardButtonClicked(`banner-${offerName}`, 'mainAction');
          onBannerButtonClick(mainButtonConfig, contactId);
        }}
        onSecActionClicked={(contactId) => {
          biLogger.reportCardButtonClicked(
            `banner-${offerName}`,
            'secondaryAction',
          );
          onBannerButtonClick(secondaryButtonConfig!, contactId);
        }}
        video={video}
        image={illustration}
        mainButtonConfig={mainButtonConfig}
        secondaryButtonConfig={secondaryButtonConfig}
      />
    );
  };

  return (
    <Page
      scrollProps={{
        onScrollAreaChanged: (scrollData: ScrollAreaData) => {
          if (
            YAreas.findIndex((area) => area === scrollData.area.y) <
            YAreas.findIndex((area) => area === lastAreaY)
          ) {
            biLogger.reportScrollingPage();
          }
          lastAreaY = scrollData.area.y;
        },
      }}
    >
      <Page.Header
        dataHook={datahooks.homeHeader}
        actionsBar={
          <Box>
            <Box marginRight="18px">
            {(shouldShowCrmVideo ? (<Tooltip
                dataHook={datahooks.crmVideoPageHeaderButtonTooltip}
                content={t('crmHomePage.watchCRMVideo.tip')}>
                <IconButton
                  onClick={() => openDemoVideo(DemoVideoType.CRM)}
                  dataHook={datahooks.crmVideoPageHeaderButton}
                  skin="inverted"
                >
                  <Play />
                </IconButton>
              </Tooltip>)  : null )}
            </Box>
            <Box marginRight="18px">
              <CreateNewMenu biLogger={biLogger} />
            </Box>
            <AscendUpgradeButton
              buttonText={t('crmHomePage.upgradeAscend')}
              slug={CRM_HOME}
              origin={CRM_HOME_ORIGIN}
              items={[
                t('crmHomePage.ascendUpgrade.saveTime'),
                t('crmHomePage.ascendUpgrade.customize'),
                t('crmHomePage.ascendUpgrade.getMore'),
              ]}
            />
          </Box>
        }
        breadcrumbs={(minimized) => (
                <Breadcrumbs
                  activeId="1"
                  items={[
                    {
                      id: '1',
                      value: (
                        <AscendBadge
                          slug={CRM_HOME}
                          title={t('crmHomePage.header.title')}
                          minimized={minimized}
                        />
                      ),
                    },
                  ]}
                  onClick={() => {}}
                />
              )}
        title={t('crmHomePage.header.title')}
        subtitle={t('crmHomePage.header.subtitle')}
      />
      <Page.Content>
        <Container>
          <Row>
            <Col>
              <Card>{renderBanner()}</Card>
            </Col>
          </Row>
          {!phase2NewFeatures && (
            <OpportunitySection
              biLogger={biLogger}
              opportunities={props.cards}
              onOpportunityActionClick={(btn) => onBannerButtonClick(btn)}
            />
          )}
          {phase2NewFeatures && (
            <CrmHomeSection
              titleDataHook={datahooks.homeContactsSectionTitle}
              title={t('crmHomePage.contacts.sectionTitle')}
            >
              <LazyWidgetCard>
                <ContactsWidget
                  {...widgetInitData.contacts}
                  mainButtonText={t('crmHomePage.contacts.sendMessage')}
                  mainButtonAction={(id: string) => {
                    biLogger.reportSendMessage(id);
                    navigateTo({
                      pageComponentId: PageComponentId.Engage,
                      contextData: {
                        appState: id,
                        referrer: 'crm-home',
                      },
                    });
                  }}
                  secondaryButtonText={t('crmHomePage.contacts.viewContact')}
                  secondaryButtonAction={(id: string) => {
                    biLogger.reportViewContact(id);
                    navigateTo({
                      pageComponentId: PageComponentId.ContactFullPage,
                      contextData: {
                        appState: id,
                        referrer: 'crmHome',
                      },
                    });
                  }}
                />
              </LazyWidgetCard>
            </CrmHomeSection>
          )}
          <CrmHomeSection
            titleDataHook={datahooks.homeAutomationsSectionTitle}
            title={t('crmHomePage.automations.sectionTitle')}
            actionTitle={t('crmHomePage.automations.sectionTitleAction')}
            actionOnClick={() => onCreateNewAutomation()}
          >
            <AutomationsDashboard {...AutomationsDashboardProps} />
          </CrmHomeSection>
          {phase2NewFeatures && (
            <>
              <CrmHomeSection
                titleDataHook={datahooks.homeTasksSectionTitle}
                title={t('crmHomePage.tasks.sectionTitle')}
              >
                <LazyWidgetCard>
                  <TasksWidget {...moduleParams} />
                </LazyWidgetCard>
              </CrmHomeSection>
              <WorkflowsSection
                api={props.services.workflows}
                biLogger={biLogger}
              />
              <Integrations
                biLogger={biLogger}
                connections={props.connections}
                financeIntegrationData={props.financeIntegrationData}
              />
            </>
          )}
          <CrmHomeSection
            titleDataHook={datahooks.homeInvoicesSectionTitle}
            title={t('crmHomePage.finances.sectionTitle')}
          >
            <FinanceSection
              biLogger={biLogger}
              onNavigate={() => {
                biLogger.reportFinancesCardButtonClicked('invoices');
                navigateTo({
                  pageComponentId: PageComponentId.Invoices,
                  viewId: 'create-invoice',
                  contextData: {
                    appState: 'create',
                    referrer: 'crmHome',
                  },
                });
              }}
            />
          </CrmHomeSection>
          {phase2NewFeatures && (
            <OpportunitySection
              biLogger={biLogger}
              opportunities={props.cards}
              onOpportunityActionClick={(btn) => onBannerButtonClick(btn)}
            />
          )}
          <CrmHomeSection
            titleDataHook={datahooks.homeAllToolsSectionTitle}
            title={t('crmHomePage.allTools.sectionTitle')}
            actionTitle={shouldShowCrmVideo ? ('\u25BA ' + t('crmHomePage.watchCRMVideo.link')) : undefined }
            actionOnClick={shouldShowCrmVideo ? (() => openDemoVideo(DemoVideoType.CRM)) : undefined }
          >
            <Container>
              <Row>
                <Col span={4}>
                  <ToolCard
                    toolName={tools.contacts.name}
                    onClick={() =>
                      logAndNavigate(tools.contacts, sections.allTools)
                    }
                  />
                </Col>
                <Col span={4}>
                  <ToolCard
                    toolName={tools.inbox.name}
                    onClick={() =>
                      logAndNavigate(tools.inbox, sections.allTools)
                    }
                  />
                </Col>
                <Col span={4}>
                  <ToolCard
                    toolName={tools.forms.name}
                    onClick={() =>
                      logAndNavigate(tools.forms, sections.allTools)
                    }
                  />
                </Col>
              </Row>
              <Row>
                <Col span={4}>
                  <ToolCard
                    toolName={tools.automations.name}
                    onClick={() =>
                      logAndNavigate(tools.automations, sections.allTools)
                    }
                  />
                </Col>
                <Col span={4}>
                  <ToolCard
                    toolName={tools.members.name}
                    onClick={() =>
                      logAndNavigate(tools.members, sections.allTools)
                    }
                  />
                </Col>
                <Col span={4}>
                  <ToolCard
                    toolName={tools.tasks.name}
                    onClick={() =>
                      logAndNavigate(tools.tasks, sections.allTools)
                    }
                  />
                </Col>
              </Row>
              <Row>
                <Col span={4}>
                  <ToolCard
                    toolName={tools.invoices.name}
                    onClick={() =>
                      logAndNavigate(tools.invoices, sections.allTools)
                    }
                  />
                </Col>
                <Col span={4}>
                  <ToolCard
                    toolName={tools.priceQuotes.name}
                    onClick={() =>
                      logAndNavigate(tools.priceQuotes, sections.allTools)
                    }
                  />
                </Col>
                <Col span={4}>
                  <ToolCard
                    toolName={tools.workflows.name}
                    onClick={() =>
                      logAndNavigate(tools.workflows, sections.allTools)
                    }
                  />
                </Col>
              </Row>
              {phase2NewFeatures && (
                <Row>
                  <Col>
                    <Box verticalAlign="middle">
                      <Box marginRight="8px">
                        <Promote />
                      </Box>
                      <Text dataHook={datahooks.homePromote}>
                        <Trans
                          i18nKey="crmHomePage.marketing.promoteHome"
                          t={props.t}
                          components={[
                            <TextButton
                              dataHook={datahooks.homePromoteLink}
                              onClick={() => {
                                biLogger.reportGoToMarketing();
                                navigateTo({
                                  pageComponentId: PageComponentId.PromoteHome,
                                  contextData: {
                                    referrer: 'crmHome',
                                  },
                                });
                              }}
                            >
                              {' '}
                            </TextButton>,
                          ]}
                        />
                      </Text>
                    </Box>
                  </Col>
                </Row>
              )}
            </Container>
          </CrmHomeSection>
        </Container>
        {mapDemoVideoStateToUrls() && renderPromoVideoModal()}
      </Page.Content>
    </Page>
  );
};
export default translate()(CrmHomeComponent);
